import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { OUTCOME_META, TYPE_COLOR, nextRunText } from "./outcomes";

export const MAX_USAGE_KWH = 10000;
export const CHARGE_CAP = 1000;
const BILL_TYPE_LABELS = { FINAL_BILL: "Final bill", FINAL: "Final bill", BILL: "Regular bill", STANDARD: "Standard", ESTIMATE: "Estimate" };

export function money(value) {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

// The database stores timestamps (created, updated, processed) in UTC without a zone, so they're read as UTC
// and shown in the viewer's local time. Move dates are calendar dates and aren't converted.
export const fromUtc = (value) => {
  if (!value || String(value).startsWith("0000-00-00")) return null;
  const parsed = moment.utc(value, "YYYY-MM-DD HH:mm:ss");
  return parsed.isValid() ? parsed.local() : null;
};

export const kwh = (value) => (value == null || value === "" ? "—" : `${Number(value).toLocaleString()} kWh`);
const dayOf = (value) => (value ? String(value).slice(0, 10) : null);
export const shortDate = (value) => (value ? moment(dayOf(value), "YYYY-MM-DD").format("MMM D") : "—");
const longDate = (value) => (value ? moment(dayOf(value), "YYYY-MM-DD").format("MMM D, YYYY") : "—");

export function relativeDay(date, today) {
  const diff = moment(dayOf(date), "YYYY-MM-DD").diff(moment(today, "YYYY-MM-DD"), "days");
  if (diff === 0) return "today";
  return diff < 0 ? `${-diff}d ago` : `in ${diff}d`;
}

// Same day count as periodDays in batch-moves.js: 1 is a move on the read's date, 2 the day after.
// The batch processes a move without a final bill at this many days or fewer: billing skips one-day final bills
// (BLPLS-407).
export const UNBILLED_PERIOD_DAYS = 2;

export function billPeriodDays(start, end) {
  const days = moment(dayOf(end), "YYYY-MM-DD").diff(moment(dayOf(start), "YYYY-MM-DD"), "days");
  return days === 0 ? 1 : days + 1;
}

// The estimate's own reads once it exists. Before that, the bill would run from the last billed read to the move date,
// unless the meter was already read on or after the move date: the batch then has nothing to final-bill (or flags the
// move when that read isn't billed yet).
export function billPeriod({ bill, meterReads, moveDate, isProcessed }) {
  if (bill && bill.available && bill.matched && bill.prevReadDate && bill.currReadDate) {
    return { start: bill.prevReadDate, end: bill.currReadDate, startRead: bill.prevReadAmt, endRead: bill.currReadAmt, projected: false };
  }
  if (isProcessed || !moveDate) return null;
  const reads = meterReads || [];
  const latest = reads[0];
  if (latest && dayOf(latest.readAt) >= moveDate) {
    return { start: dayOf(latest.readAt), end: moveDate, startRead: latest.readAmount, endRead: null, projected: true, readAfterMove: latest };
  }
  const lastBilled = reads.find((r) => Number(r.billed) === 1);
  if (!lastBilled) return null;
  return { start: dayOf(lastBilled.readAt), end: moveDate, startRead: lastBilled.readAmount, endRead: null, projected: true };
}

export function Panel({ title, action, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, ...sx }}>
      {title || action ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25, minHeight: 24 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}>
            {title}
          </Typography>
          {action}
        </Box>
      ) : null}
      {children}
    </Paper>
  );
}

export function KeyValues({ rows }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "max-content minmax(0, 1fr)", columnGap: 2, rowGap: 0.6 }}>
      {rows.filter(Boolean).map(([label, value]) => (
        <React.Fragment key={label}>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{label}</Typography>
          <Typography component="div" sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
            {value == null || value === "" ? "—" : value}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

// A deleted move-in whose rival on the unit was already processed: that move-in opened an account that now holds the
// unit, so a restored move-in is flagged "unit occupied" until that account moves out.
export function processedRivalText(rival) {
  if (!rival) return null;
  const name = rival.name || "The kept move-in";
  const when = rival.dateProcessed ? ` on ${moment.utc(rival.dateProcessed, "YYYY-MM-DD HH:mm:ss").local().format("MMM D")}` : "";
  const account = rival.accountNum ? `account #${rival.accountNum}` : "an account";
  return `${name}'s move-in was already processed${when} and opened ${account}, which now holds the unit. `
    + `Restored, this move-in is flagged "unit occupied" until that account moves out. If ${rival.name || "that"} was the wrong person, `
    + `restore this one, then use "Create the tenant's move-out" on its page to move ${account} out.`;
}

export function VerdictBar({ verdict, extra, actions }) {
  const meta = OUTCOME_META[verdict.outcome] || OUTCOME_META.upcoming;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        borderRadius: 1,
        bgcolor: meta.bg,
        borderLeft: `4px solid ${meta.color}`,
      }}
    >
      <Box sx={{ flex: "1 1 340px", minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {verdict.busy ? <CircularProgress size={14} sx={{ color: meta.color }} /> : null}
          <Typography sx={{ fontWeight: 600, color: meta.color, fontSize: 15 }}>{verdict.label}</Typography>
        </Box>
        {verdict.reason ? <Typography sx={{ fontSize: 13.5, mt: 0.25 }}>{verdict.reason}</Typography> : null}
        {extra}
      </Box>
      {actions ? <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>{actions}</Box> : null}
    </Box>
  );
}

const CHECK_STYLE = {
  pass: { color: "#2e7d32", border: "#cfe5d0", bg: "transparent", Icon: CheckCircleIcon },
  fail: { color: "#c62828", border: "#f1c4c0", bg: "#fdeceb", Icon: ErrorIcon },
  warn: { color: "#9a6400", border: "#ecd9a8", bg: "#fbf1dc", Icon: WarningAmberIcon },
  info: { color: "#5f6670", border: "#dfe2e6", bg: "transparent", Icon: InfoOutlinedIcon },
};

// The checks the nightly batch runs, in its order, so the analyst sees which one decides the outcome.
// No unit is ever vacant: between tenants the PM holds it, as a Management Co account, so it keeps billing.
export const occupantRole = (customerType) => (customerType === "Management Co" ? "PM" : customerType || "Occupant");

export function buildBatchChecks({ kind, move, prediction, bill, period, today, nextRun, providedOtherReads }) {
  const checks = [];
  const code = prediction?.code;
  // The batch runs at 2 AM Eastern, Tuesday through Friday, and takes the moves dated at least a day before the run.
  const runDate = prediction?.runDate || moment(move.moveDate, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
  const tonight = nextRun ? runDate === nextRun.date : move.moveDate < today;
  checks.push(tonight
    ? {
      state: "pass",
      label: nextRunText(nextRun) === "tonight" ? "In tonight's run" : "In the next run",
      detail: nextRun
        ? `The 2 AM run on ${longDate(nextRun.date)} takes moves dated through ${longDate(nextRun.takesThroughDate)}`
        : `${longDate(move.moveDate)}: tonight's batch can pick it up`,
    }
    : { state: "info", label: `Runs ${moment(runDate, "YYYY-MM-DD").format("ddd, MMM D")}, 2 AM`, detail: "The batch runs at 2 AM Eastern, Tuesday through Friday, and takes moves dated at least a day before the run" });
  if (Number(move.issues) === 1) checks.push({ state: "fail", label: "Flagged", detail: "The batch skips flagged moves until the flag is cleared" });
  else checks.push({ state: "pass", label: "Not flagged" });
  if (Number(move.onHold) === 1) checks.push({ state: "warn", label: "On hold", detail: "The batch skips it until the hold is released" });
  else checks.push({ state: "pass", label: "Not on hold" });

  if (kind === "in") {
    if (code === "DUPLICATE") checks.push({ state: "fail", label: "Duplicate move-in", detail: prediction.reason });
    if (code === "STALE") checks.push({ state: "fail", label: "Over 2 months old", detail: prediction.reason });
    if (code === "UNIT_OCCUPIED") checks.push({ state: "fail", label: "Unit occupied", detail: prediction.reason });
    else if (code === "OCCUPANT_DUPLICATE") checks.push({ state: "fail", label: "Likely duplicate", detail: prediction.reason });
    else if (move.occupantCustomerType === "Tenant") checks.push({ state: "pass", label: "Tenant moves out first", detail: prediction?.handoff || null });
    else if (move.occupantAccountNum) {
      checks.push({
        state: "pass",
        label: `${occupantRole(move.occupantCustomerType)} moves out`,
        detail: `${move.occupantName || `Account #${move.occupantAccountNum}`} (${move.occupantCustomerType || "occupant"}, #${move.occupantAccountNum}) holds the unit and gets the final bill`,
      });
    }
  }
  if (kind === "out" && code === "PM_HANDOFF_FAILS") checks.push({ state: "warn", label: "PM can't move in", detail: prediction.reason });

  if (code === "WAITS_FOR_MOVE_OUT_BILL") checks.push({ state: "info", label: "Waits for the move-out's bill", detail: prediction.reason });
  if (code === "MOVE_OUT_BILL_PENDING") checks.push({ state: "fail", label: "Move-out bill not out", detail: prediction.reason });
  if (code === "ESTIMATE_OVERLAP") checks.push({ state: "fail", label: "Estimate overlaps a move-out", detail: prediction.reason });
  if (code === "ROLLOVER_ESTIMATED") checks.push({ state: "warn", label: "Rollover not billed by the batch", detail: prediction.reason });
  if (code === "ESTIMATE_REPLACED") checks.push({ state: "warn", label: "Batch replaces the estimate", detail: prediction.reason });

  const read = move.meterRead != null ? Number(move.meterRead) : null;
  if (read > 0) checks.push({ state: "pass", label: `${move.readAmtType === "EST" ? "Estimated" : "Actual"} read ${read.toLocaleString()}`, detail: "Read provided with the move" });
  else checks.push({ state: "info", label: "No move read", detail: "The final bill estimates usage from the meter's history" });

  if (prediction?.otherMeter) checks.push({ state: "fail", label: "Other meter's read", detail: prediction.reason });
  (providedOtherReads || []).forEach((r) => checks.push({
    state: "pass",
    label: `${Number(r.meterCode) === 2 ? "Garage" : "Second meter"} read ${Number(r.readAmount).toLocaleString()}`,
    detail: `Provided for the move on meter #${r.meterNum}. The final bill uses it.`,
  }));

  if (period && period.readAfterMove) {
    const billed = Number(period.readAfterMove.billed) === 1;
    checks.push(billed
      ? { state: "info", label: `Billed through ${shortDate(period.start)}`, detail: `The meter was read and billed on ${shortDate(period.start)}, on or after the move date, so there's no final bill` }
      : { state: "fail", label: "Unbilled read after move", detail: `The ${shortDate(period.start)} read is on or after the move date and isn't billed yet, so the batch flags the move` });
  } else if (period) {
    const days = billPeriodDays(period.start, period.end);
    checks.push(days <= UNBILLED_PERIOD_DAYS
      ? { state: "info", label: "A day or less to bill", detail: `The bill period would run ${shortDate(period.start)} to ${shortDate(period.end)}, so the batch processes it without a bill` }
      : { state: "pass", label: `${days}-day bill period`, detail: `${shortDate(period.start)} to ${shortDate(period.end)}${period.projected ? " (projected)" : ""}` });
  }

  if (bill && bill.available && bill.matched && !bill.singleBill) {
    const usage = bill.usage != null ? Number(bill.usage) : null;
    if (usage != null) {
      if (usage < 0) checks.push({ state: "fail", label: "Negative usage", detail: `${usage.toLocaleString()} kWh: the reads went down` });
      else if (usage > MAX_USAGE_KWH) checks.push({ state: "fail", label: `Over ${MAX_USAGE_KWH.toLocaleString()} kWh`, detail: `${usage.toLocaleString()} kWh. Verify the reads.` });
      else checks.push({ state: "pass", label: `Usage ${usage.toLocaleString()} kWh` });
    }
    const total = bill.total != null ? Number(bill.total) : null;
    if (total != null) {
      checks.push(total > CHARGE_CAP
        ? { state: "warn", label: `Over the ${money(CHARGE_CAP)} cap`, detail: "The batch flags this for review. You can still bill it here after checking it." }
        : { state: "pass", label: `Under the ${money(CHARGE_CAP)} cap` });
    }
  }
  return checks;
}

export function ChecksStrip({ checks }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
      {checks.map((check) => {
        const style = CHECK_STYLE[check.state];
        const { Icon } = style;
        return (
          <Tooltip key={check.label} title={check.detail || ""} arrow disableHoverListener={!check.detail}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                border: `1px solid ${style.border}`,
                bgcolor: style.bg,
                color: style.color,
                fontSize: 12,
                lineHeight: 1.6,
                whiteSpace: "nowrap",
              }}
            >
              <Icon sx={{ fontSize: 14 }} />
              {check.label}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function useElementWidth(fallback) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

// One axis for everything that decides the final bill: the unit's moves above the line, the meter's reads on it
// (each labeled with its date and value, and the kWh used since the read before), and the bill period shaded.
export function UnitTimeline({ kind, moveDate, outcome, unitMoves, reads, otherMeters, period, today }) {
  const [ref, measured] = useElementWidth(700);
  const width = Math.max(320, measured);
  const height = 120;
  const padX = 34;
  const topY = 11;
  const movesY = 32;
  const axisY = 56;
  const dateY = 74;
  const amountY = 87;
  const todayY = 112;

  const ordered = (reads || []).slice().reverse();
  const earliestWanted = moment(period ? dayOf(period.start) : moveDate, "YYYY-MM-DD").subtract(75, "days").format("YYYY-MM-DD");
  let shownReads = ordered.filter((r) => dayOf(r.readAt) >= earliestWanted);
  if (shownReads.length < 3) shownReads = ordered.slice(-3);

  // Points on the axis: the stored reads, plus the bill's end read when it isn't stored yet.
  const points = shownReads.map((r) => ({
    key: `${r.readAt}-${r.meterNum}`,
    date: dayOf(r.readAt),
    amount: Number(r.readAmount),
    billed: Number(r.billed) === 1,
    estimate: r.readType === "EST",
  }));
  if (period && !period.readAfterMove && !points.some((p) => p.date === dayOf(period.end))) {
    points.push({ key: "end", date: dayOf(period.end), amount: period.endRead != null ? Number(period.endRead) : null, billed: false, estimate: true, pending: true });
  }
  points.sort((a, b) => a.date.localeCompare(b.date));

  const dates = [moveDate, today, ...unitMoves.map((m) => m.moveDate), ...points.map((p) => p.date)];
  if (period) dates.push(dayOf(period.start), dayOf(period.end));
  const valid = dates.filter(Boolean).sort();
  const start = moment(valid[0], "YYYY-MM-DD").subtract(4, "days");
  const end = moment(valid[valid.length - 1], "YYYY-MM-DD").add(4, "days");
  const span = Math.max(1, end.diff(start, "days"));
  const x = (date) => padX + (moment(dayOf(date), "YYYY-MM-DD").diff(start, "days") / span) * (width - 2 * padX);

  // Label the reads that matter most first, then any others that fit.
  const minGap = 62;
  const labelled = new Set();
  const placed = [];
  const tryLabel = (p) => {
    if (!p || labelled.has(p.key)) return;
    const px = x(p.date);
    if (placed.some((other) => Math.abs(other - px) < minGap)) return;
    labelled.add(p.key);
    placed.push(px);
  };
  const periodStart = period && points.find((p) => p.date === dayOf(period.start));
  const periodEnd = period && points.find((p) => p.date === dayOf(period.end));
  [periodStart, periodEnd, points[points.length - 1]].forEach(tryLabel);
  points.slice().reverse().forEach(tryLabel);

  const days = period && !period.readAfterMove ? billPeriodDays(period.start, period.end) : null;
  const thisMeta = OUTCOME_META[outcome] || OUTCOME_META.upcoming;
  const moveX = x(moveDate);
  const moveLabel = `${kind === "in" ? "Move-in" : "Move-out"} ${shortDate(moveDate)}`;
  const moveLabelRight = moveX < width - 130;
  const triangle = (cx, cy, size, up) => (up
    ? `M${cx - size} ${cy + size * 0.7} L${cx + size} ${cy + size * 0.7} L${cx} ${cy - size} Z`
    : `M${cx - size} ${cy - size * 0.7} L${cx + size} ${cy - size * 0.7} L${cx} ${cy + size} Z`);
  const usageLabel = (a, b) => {
    if (a.amount == null || b.amount == null) return null;
    const used = b.amount - a.amount;
    return { text: `${used >= 0 ? "+" : "−"}${Math.abs(used).toLocaleString()} kWh`, bad: used < 0 };
  };
  const hasEstimate = points.some((p) => p.estimate && !p.pending);
  const hasUnbilled = points.some((p) => !p.billed && !p.pending);

  return (
    <Box>
      <svg ref={ref} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }} role="img"
        aria-label="Unit timeline: the meter's reads with dates and values, the bill period, and the moves on this unit">
        {period && !period.readAfterMove ? (
          <>
            <rect x={x(period.start)} y={axisY - 12} width={Math.max(3, x(period.end) - x(period.start))} height={24}
              fill={days <= UNBILLED_PERIOD_DAYS ? "rgba(198, 40, 40, 0.10)" : "rgba(91, 67, 201, 0.10)"} rx={2} />
            <text x={(x(period.start) + x(period.end)) / 2} y={topY} fontSize={11} textAnchor="middle" fill="#5b43c9">
              {`${period.projected ? "Projected bill period" : "Bill period"} ${shortDate(period.start)} → ${shortDate(period.end)} · ${days} day${days === 1 ? "" : "s"}`}
            </text>
          </>
        ) : period && period.readAfterMove ? (
          <text x={x(period.start)} y={topY} fontSize={11} textAnchor="middle" fill="#c62828">Read after the move date</text>
        ) : null}

        <line x1={padX - 10} x2={width - padX + 10} y1={axisY} y2={axisY} stroke="#d5d9de" />

        {points.slice(1).map((p, i) => {
          const prev = points[i];
          const mid = (x(prev.date) + x(p.date)) / 2;
          const usage = usageLabel(prev, p);
          if (!usage || x(p.date) - x(prev.date) < 58) return null;
          const inPeriod = period && prev.date === dayOf(period.start);
          return (
            <text key={`use-${p.key}`} x={mid} y={axisY - 6} fontSize={10.5} textAnchor="middle"
              fill={usage.bad ? "#c62828" : inPeriod ? "#5b43c9" : "#8b929b"}>
              {usage.text}
            </text>
          );
        })}

        <line x1={x(today)} x2={x(today)} y1={movesY - 12} y2={todayY - 11} stroke="#8b929b" strokeDasharray="3 3" />
        <text x={x(today)} y={todayY} fontSize={10.5} textAnchor="middle" fill="#5f6670">{`Today ${shortDate(today)}`}</text>

        <line x1={moveX} x2={moveX} y1={movesY + 6} y2={axisY + 12} stroke={TYPE_COLOR[kind]} strokeWidth={1.5} />
        {unitMoves.map((m) => {
          const meta = OUTCOME_META[m.outcome] || OUTCOME_META.upcoming;
          return (
            <path key={`${m.kind}-${m.uuid}`} d={triangle(x(m.moveDate), movesY, 5, m.kind === "in")} fill={meta.color} stroke="#fff" strokeWidth={1}>
              <title>{`${m.kind === "in" ? "Move-in" : "Move-out"} · ${m.name || "—"} · ${shortDate(m.moveDate)} · ${m.label}`}</title>
            </path>
          );
        })}
        <path d={triangle(moveX, movesY, 7.5, kind === "in")} fill={thisMeta.color} stroke="#1f2328" strokeWidth={1.2}>
          <title>{`This ${kind === "in" ? "move-in" : "move-out"} · ${shortDate(moveDate)}`}</title>
        </path>
        <text x={moveLabelRight ? moveX + 12 : moveX - 12} y={movesY + 4} fontSize={11.5} fontWeight={600}
          textAnchor={moveLabelRight ? "start" : "end"} fill={TYPE_COLOR[kind]}>
          {moveLabel}
        </text>

        {points.map((p) => {
          const px = x(p.date);
          const tip = p.pending
            ? `${shortDate(p.date)} · end read, estimated when the move is queued`
            : `${shortDate(p.date)} · ${p.amount.toLocaleString()} kWh · ${p.estimate ? "estimate" : "actual"} · ${p.billed ? "billed" : "not billed yet"}`;
          return (
            <g key={p.key}>
              <circle cx={px} cy={axisY} r={5} fill={p.billed ? "#5f6670" : "#fff"} stroke={p.pending ? "#5b43c9" : "#5f6670"} strokeWidth={1.5}
                strokeDasharray={p.estimate ? "2 1.6" : undefined}>
                <title>{tip}</title>
              </circle>
              {labelled.has(p.key) ? (
                <>
                  <text x={px} y={dateY} fontSize={11} fontWeight={500} textAnchor="middle" fill="#3d434a">{shortDate(p.date)}</text>
                  <text x={px} y={amountY} fontSize={10.5} textAnchor="middle" fill={p.pending ? "#5b43c9" : "#5f6670"}>
                    {p.pending ? (p.amount != null ? p.amount.toLocaleString() : "estimated when queued") : `${p.amount.toLocaleString()}${p.estimate ? " est." : ""}`}
                  </text>
                </>
              ) : null}
            </g>
          );
        })}
      </svg>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
        ● billed read{hasUnbilled ? " · ○ not billed yet" : ""}{hasEstimate || points.some((p) => p.pending) ? " · dashed = estimate" : ""} · kWh between reads shown above the line
        {unitMoves.length ? " · other moves on this unit: hover the markers" : ""}
      </Typography>
      {(otherMeters || []).map((m) => (
        <Typography key={m.meterNum} sx={{ fontSize: 11, color: "text.secondary" }}>
          {`Also on this unit: ${Number(m.meterCode) === 2 ? "garage" : "second"} meter #${m.meterNum}, last read ${shortDate(m.readAt)} `}
          {`(${Number(m.readAmount).toLocaleString()}${Number(m.billed) === 1 ? ", billed" : ", not billed yet"}). The final bill includes it.`}
        </Typography>
      ))}
    </Box>
  );
}

function Fact({ label, value, sub, tone }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: tone === "bad" ? "#c62828" : "text.primary", fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      {sub ? <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{sub}</Typography> : null}
    </Box>
  );
}

const meterLabel = (meter) => `${Number(meter.meterCode) === 2 ? "Garage" : "Main"} meter #${meter.meterNum}`;

// A two-meter unit's bill lines, grouped under each meter, then the charges outside them (fees).
function groupLinesByMeter(lines, meters) {
  const meterNums = [...new Set(lines.map((line) => line.meterNum).filter(Boolean))];
  if (meterNums.length < 2) return [{ key: "all", title: null, lines }];
  const order = (meters || []).map((m) => String(m.meterNum));
  meterNums.sort((a, b) => (order.indexOf(String(a)) + 1 || 99) - (order.indexOf(String(b)) + 1 || 99));
  const byNum = new Map((meters || []).map((m) => [String(m.meterNum), m]));
  const groups = meterNums.map((num) => ({
    key: num,
    title: meterLabel(byNum.get(String(num)) || { meterNum: num }),
    lines: lines.filter((line) => line.meterNum === num),
  }));
  const other = lines.filter((line) => !line.meterNum);
  if (other.length) groups.push({ key: "other", title: "Other charges", lines: other });
  return groups;
}

function PreviewLines({ preview, meters }) {
  return (
    <Box>
      {preview.bills.map((b) => (
        <Box key={b.accountNum}>
          {preview.bills.length > 1 ? <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>Acct #{b.accountNum}</Typography> : null}
          {groupLinesByMeter(b.lines, meters).map((group) => (
            <Box key={`${b.accountNum}-${group.key}`}>
              {group.title ? <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.75 }}>{group.title}</Typography> : null}
              {group.lines.map((line, i) => (
                <Box key={`${b.accountNum}-${group.key}-${i}`} sx={{ display: "flex", justifyContent: "space-between", py: 0.2 }}>
                  <Typography sx={{ fontSize: 13 }}>{line.description}</Typography>
                  <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{money(line.amount)}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      ))}
      <Divider sx={{ my: 0.75 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>New charges</Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{money(preview.newCharges)}</Typography>
      </Box>
      {preview.bills.length === 1 && Number(preview.bills[0].balanceForward) !== 0 ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "right" }}>
          Balance forward {money(preview.bills[0].balanceForward)} · total due {money(preview.bills[0].total)}
        </Typography>
      ) : null}
    </Box>
  );
}

function accountLink(accountNum, accountUuid) {
  return accountUuid ? `/accountmanager/${accountNum}/${accountUuid}` : `/accountmanager/${accountNum}`;
}

// One row per meter on a two-meter unit's final bill: there's one bill, with a line for each meter.
function MeterLines({ bill, isProcessed }) {
  const showBilled = isProcessed && bill.accountBillUuid;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.25 }}>Each meter on this bill</Typography>
      {bill.meters.map((m) => (
        <Box key={m.meterNum} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr auto", sm: "minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1.3fr) 0.8fr 0.7fr" }, columnGap: 1.5, py: 0.4, borderTop: "1px solid", borderColor: "divider", alignItems: "baseline" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{meterLabel(m)}</Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", display: { xs: "none", sm: "block" } }}>
            {m.prevReadDate ? `${shortDate(m.prevReadDate)} → ${shortDate(m.currReadDate)}` : "—"}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", display: { xs: "none", sm: "block" } }}>
            {m.prevReadAmt != null ? `${Number(m.prevReadAmt).toLocaleString()} → ${Number(m.currReadAmt).toLocaleString()}` : "—"}
            {m.prevReadType ? ` · ${m.prevReadType} → ${m.currReadType || "—"}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 13, display: { xs: "none", sm: "block" }, color: Number(m.usage) < 0 ? "#c62828" : undefined }}>{kwh(m.usage)}</Typography>
          <Typography sx={{ fontSize: 13, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
            {money(showBilled && m.billed != null ? m.billed : m.totalCharge)}
          </Typography>
        </Box>
      ))}
      {showBilled ? (bill.otherCharges || []).map((c) => (
        <Box key={c.description} sx={{ display: "flex", justifyContent: "space-between", py: 0.4, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography sx={{ fontSize: 13 }}>{c.description}</Typography>
          <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{money(c.amount)}</Typography>
        </Box>
      )) : null}
    </Box>
  );
}

// No final bill came from the estimate, but other bills on its meters cover some or all of its period, so Retry billing
// is off: the account's own (usually the monthly run billing the moved-out account), or another account's on the unit,
// such as the next tenant's first bill starting from the read before the move.
function CoveredBy({ covered }) {
  const one = covered.bills.length === 1;
  const others = covered.otherAccounts || [];
  const accountOf = (b) => String(b.accountNum ?? covered.accountNum);
  const toWhom = others.length
    ? `${others.length === 1 ? `#${others[0]}` : "other accounts"}${covered.bills.some((b) => accountOf(b) === String(covered.accountNum)) ? ` and #${covered.accountNum}` : ""}`
    : `#${covered.accountNum}`;
  return (
    <Alert severity={covered.full ? "info" : "warning"} sx={{ py: 0.25 }}>
      <Typography sx={{ fontSize: 13 }}>
        {others.length ? (
          <>
            No bill came from this estimate for #{covered.accountNum} ({shortDate(covered.estimateFrom)} → {shortDate(covered.estimateTo)}).
            {" "}{covered.full ? "Its days were" : "Some of its days were"} billed to {toWhom} instead.
          </>
        ) : (
          <>
            No bill came from this estimate ({shortDate(covered.estimateFrom)} → {shortDate(covered.estimateTo)}), but
            {one ? " this bill" : " these bills"} to {toWhom} {covered.full ? "already " : ""}{one ? "covers" : "cover"}{covered.full ? " it" : " part of it"}.
          </>
        )}
        {covered.full ? " Retrying would bill it twice." : " Retrying would bill those days twice, so bill the rest by hand."}
      </Typography>
      {covered.bills.map((b) => (
        <Typography key={b.billUuid} sx={{ fontSize: 13, mt: 0.5 }}>
          {others.length ? `#${accountOf(b)} · ` : ""}{BILL_TYPE_LABELS[b.type] || b.type || "Bill"} · billed {longDate(b.billDate)} · {shortDate(b.serviceStart)} → {shortDate(b.serviceEnd)}
          {b.newCharges != null ? ` · ${money(b.newCharges)} in new charges` : ""}
          {b.pdfUrl ? <> · <Link href={b.pdfUrl} target="_blank" rel="noopener noreferrer">View PDF</Link></> : null}
        </Typography>
      ))}
    </Alert>
  );
}

// No final bill came from the estimate, and its meters are billed past it, so billing has moved on and Retry billing is
// off. Billing bills any of its days that still need it by hand.
function BilledPast({ past }) {
  return (
    <Alert severity="info" sx={{ py: 0.25 }}>
      <Typography sx={{ fontSize: 13 }}>
        Billing has moved past this move: its meter is billed through {shortDate(past.through)}, after the
        estimate's {shortDate(past.estimateFrom)} → {shortDate(past.estimateTo)}, so it can't be retried.
        Bill any of those days that still need it by hand.
      </Typography>
    </Alert>
  );
}

export function BillPanel({ kind, move, bill, billPreview, moveBill, billing, billingTarget, prediction, period, isProcessed, children }) {
  const hasEstimate = bill && bill.available && bill.matched;
  const multiMeter = hasEstimate && (bill.meters || []).length > 1;
  const usage = hasEstimate && bill.usage != null ? Number(bill.usage) : null;
  const usageBad = usage != null && (usage < 0 || usage > MAX_USAGE_KWH);
  const days = period ? billPeriodDays(period.start, period.end) : null;
  const target = isProcessed && moveBill
    ? { name: moveBill.name, accountNum: moveBill.accountNum, accountUuid: moveBill.accountUuid, customerType: null }
    : billingTarget;

  let charges = "—";
  let chargesSub = null;
  if (isProcessed && moveBill) {
    const newCharges = billing && billing.newCharges != null ? billing.newCharges : null;
    charges = money(newCharges != null ? newCharges : moveBill.total);
    chargesSub = newCharges != null ? "new charges" : "total due";
  }
  else if (billPreview && billPreview.newCharges != null) { charges = money(billPreview.newCharges); chargesSub = "new charges"; }
  else if (billPreview && billPreview.blocked) { charges = "No bill"; chargesSub = "blocked"; }
  else if (hasEstimate && bill.total != null) { charges = money(bill.total); chargesSub = "energy estimate"; }

  return (
    <Panel
      title="Final bill"
      action={target ? (
        <Typography sx={{ fontSize: 12.5, textAlign: "right" }}>
          {isProcessed && moveBill ? "Billed to " : "Bills "}
          <Link component={RouterLink} to={accountLink(target.accountNum, target.accountUuid)} target="_blank">
            {target.name || `Acct #${target.accountNum}`}
          </Link>
          {` · #${target.accountNum}`}{target.customerType ? ` · ${target.customerType}` : ""}
        </Typography>
      ) : null}
    >
      {children}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))" }, gap: 2, mt: 1.5, mb: 1.5 }}>
        {period && period.readAfterMove ? (
          <Fact
            label="Bill period"
            value="None"
            sub={`read ${shortDate(period.start)}, on or after the move date${Number(period.readAfterMove.billed) === 1 ? " · already billed" : " · not billed yet"}`}
            tone="bad"
          />
        ) : (
          <Fact
            label={period && period.projected ? "Projected period" : "Bill period"}
            value={period ? `${shortDate(period.start)} → ${shortDate(period.end)}` : "—"}
            sub={period ? `${days} day${days === 1 ? "" : "s"}${days <= UNBILLED_PERIOD_DAYS ? " · no bill" : ""}` : null}
            tone={days != null && days <= UNBILLED_PERIOD_DAYS ? "bad" : null}
          />
        )}
        <Fact
          label={multiMeter ? "Reads · main meter" : "Reads"}
          value={period && period.readAfterMove
            ? Number(period.startRead).toLocaleString()
            : period && period.startRead != null
            ? `${Number(period.startRead).toLocaleString()} → ${period.endRead != null ? Number(period.endRead).toLocaleString() : "?"}`
            : "—"}
          sub={hasEstimate
            ? `${bill.prevReadType || "—"} → ${bill.currReadType || "—"}`
            : period && period.readAfterMove ? `latest read, ${shortDate(period.start)}` : period ? "end read estimated when queued" : null}
        />
        <Fact
          label="Usage"
          value={usage != null ? kwh(usage) : "—"}
          sub={multiMeter ? `${bill.meters.length} meters` : hasEstimate && bill.rate != null ? `$${Number(bill.rate).toFixed(4)}/kWh` : null}
          tone={usageBad ? "bad" : null}
        />
        <Fact label="Charges" value={charges} sub={chargesSub} tone={billPreview && billPreview.blocked ? "bad" : null} />
      </Box>

      {multiMeter ? <MeterLines bill={bill} isProcessed={isProcessed} /> : null}

      {isProcessed ? (
        moveBill ? (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {BILL_TYPE_LABELS[moveBill.type] || moveBill.type || "Bill"}
              {moveBill.billDate ? ` · billed ${longDate(moveBill.billDate)}` : ""}
              {moveBill.dueDate ? ` · due ${longDate(moveBill.dueDate)}` : ""}
              {` · total due ${money(moveBill.total)}`}
            </Typography>
            {moveBill.pdfUrl ? <Link href={moveBill.pdfUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13 }}>View bill PDF →</Link> : null}
          </Box>
        ) : billing && (billing.coveredBy || billing.billedPast) ? (
          <Box sx={{ display: "grid", gap: 1 }}>
            {billing.coveredBy ? <CoveredBy covered={billing.coveredBy} /> : null}
            {billing.billedPast ? <BilledPast past={billing.billedPast} /> : null}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {move.workTableUuid ? "No bill has been created from this move's estimate." : "This move was processed without a final bill."}
          </Typography>
        )
      ) : billPreview && billPreview.blocked && billPreview.stale ? (
        <Alert severity="warning" sx={{ py: 0.25 }}>This estimate is out of date. {billPreview.blocked}</Alert>
      ) : billPreview && billPreview.blocked ? (
        <Alert severity="error" sx={{ py: 0.25 }}>No bill will be created. {billPreview.blocked}</Alert>
      ) : billPreview && billPreview.error ? (
        <Alert severity="warning" sx={{ py: 0.25 }}>{billPreview.error}</Alert>
      ) : billPreview && billPreview.bills ? (
        <PreviewLines preview={billPreview} meters={bill && bill.meters} />
      ) : move.workTableUuid && bill && bill.available && !bill.matched ? (
        <Alert severity="warning" sx={{ py: 0.25 }}>The estimate couldn't be built for this account. Unqueue and queue the move again.</Alert>
      ) : move.workTableUuid ? null : (
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          {Number(move.deleted) === 1 ? "No estimate: this move is deleted. Restore it to bill it." : period && period.readAfterMove
            ? Number(period.readAfterMove.billed) === 1
              ? `No final bill: the meter was read and billed on ${shortDate(period.start)}, on or after the move date.`
              : `The ${shortDate(period.start)} read is on or after the move date and isn't billed yet. The batch flags this move until that read is billed.`
            : prediction && /^No final bill/.test(prediction.reason || "")
              ? prediction.reason
              : `Not built yet. Queue for processing to build the estimate and see the exact charges${kind === "in" ? " for the outgoing occupant" : ""}.`}
        </Typography>
      )}
    </Panel>
  );
}

function MoveLink({ m, today }) {
  const meta = OUTCOME_META[m.outcome] || OUTCOME_META.upcoming;
  return (
    <Box
      component={RouterLink}
      to={`/move-ins-outs/${m.kind}/${m.uuid}`}
      title={m.reason || ""}
      sx={{
        display: "grid",
        gridTemplateColumns: "30px minmax(0, 1fr) auto 18px",
        alignItems: "center",
        columnGap: 1,
        py: 0.6,
        px: 0.75,
        mx: -0.75,
        borderRadius: 1,
        color: "inherit",
        textDecoration: "none",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: TYPE_COLOR[m.kind] }}>{m.kind === "in" ? "IN" : "OUT"}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13, fontWeight: 500 }}>{m.name || "—"}</Typography>
        <Typography noWrap sx={{ fontSize: 11.5, color: meta.color }}>{m.label}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "right" }}>
        {shortDate(m.moveDate)}
        <Box component="span" sx={{ display: "block", fontSize: 11 }}>{relativeDay(m.moveDate, today)}</Box>
      </Typography>
      <ChevronRightIcon fontSize="small" color="action" />
    </Box>
  );
}

// An account in one line: name (linked), number, and its type. The PM is a Management Co account.
function AccountLine({ account, warn }) {
  if (!account) return <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>Not recorded</Typography>;
  const type = account.customerType ? occupantRole(account.customerType) : null;
  return (
    <Typography sx={{ fontSize: 13.5 }}>
      {account.accountNum ? (
        <Link component={RouterLink} to={accountLink(account.accountNum, account.accountUuid)} target="_blank">
          {account.name || `Acct #${account.accountNum}`}
        </Link>
      ) : (account.name || "—")}
      {account.accountNum ? ` · #${account.accountNum}` : account.newAccount ? (type === "PM" ? " · new PM account" : " · new account") : ""}
      {type ? (
        <Box component="span" sx={{ ml: 0.75, fontSize: 11.5, px: 0.75, py: 0.1, borderRadius: 999, bgcolor: warn ? "#fdeceb" : "#eef0f2", color: warn ? "#c62828" : "#5f6670" }}>
          {type}
        </Box>
      ) : null}
      {Number(account.disconnected) === 1 ? (
        <Box component="span" sx={{ ml: 0.75, fontSize: 11.5, px: 0.75, py: 0.1, borderRadius: 999, bgcolor: "#fdeceb", color: "#c62828" }}>
          Disconnected
        </Box>
      ) : null}
    </Typography>
  );
}

const HANDOFF_SOURCE = {
  planned: () => "when it's processed",
  recorded: (handoff) => {
    const at = fromUtc(handoff.at);
    return at ? `recorded ${at.format("MMM D, h:mm A")}` : "recorded";
  },
  records: () => "from billing and PM records",
};

// Who left the unit and who held it after on this record, kept for history after the unit changes hands again.
function HandoffBlock({ kind, handoff }) {
  if (!handoff) return null;
  const row = (label, account) => (
    <Box sx={{ display: "grid", gridTemplateColumns: "28px minmax(0, 1fr)", alignItems: "baseline", columnGap: 1 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary" }}>{label}</Typography>
      <AccountLine account={account} />
    </Box>
  );
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
        This move-{kind} · {(HANDOFF_SOURCE[handoff.source] || HANDOFF_SOURCE.records)(handoff)}
      </Typography>
      {row("OUT", handoff.movedOut)}
      {row("IN", handoff.movedIn)}
      {handoff.after ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
          {handoff.after.name}'s move-out ({shortDate(handoff.after.moveDate)}) runs first and moves the PM in, so this moves the PM out.
        </Typography>
      ) : null}
      {handoff.note ? (
        <Typography sx={{ fontSize: 12, color: "#9a6400", mt: 0.25 }}>{handoff.note}</Typography>
      ) : null}
    </Box>
  );
}

export function UnitPanel({ kind, move, unitMoves, occupantMoveOuts, unitLastBill, handoff, currentOccupant, today, onCreateMoveOut, creatingMoveOut, unitHref, unitMeters = [], likelyDuplicate = false }) {
  const open = Number(move.processed) !== 1 && Number(move.deleted) !== 1;
  const tenantOccupant = kind === "in" && open && move.occupantCustomerType === "Tenant";
  return (
    <Panel
      title="Unit"
      action={unitHref ? <Link href={unitHref} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12.5 }}>Open unit ↗</Link> : null}
    >
      <Box sx={{ mb: 1.25 }}>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{unitMeters.length > 1 ? "Meters" : "Meter"}</Typography>
        <Typography sx={{ fontSize: 13.5, fontVariantNumeric: "tabular-nums" }}>
          {!unitMeters.length ? "No meter linked"
            : unitMeters.length === 1 ? `#${unitMeters[0].meterNum}`
              : unitMeters.map(meterLabel).join(" · ")}
        </Typography>
      </Box>
      <Box sx={{ mb: 1.25 }}>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Occupant now</Typography>
        {currentOccupant ? (
          <AccountLine account={currentOccupant} warn={tenantOccupant} />
        ) : (
          <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>No current occupant on record</Typography>
        )}
        {tenantOccupant && likelyDuplicate ? (
          <Typography sx={{ fontSize: 12.5, color: "#c62828", mt: 0.5 }}>Likely the same person as this move-in (same email or phone).</Typography>
        ) : tenantOccupant ? (
          occupantMoveOuts.length ? (
            <Box sx={{ mt: 0.5 }}>
              {occupantMoveOuts.map((mo) => (
                <Link key={mo.uuid} component={RouterLink} to={`/move-ins-outs/out/${mo.uuid}`} sx={{ fontSize: 12.5, display: "block" }}>
                  Their move-out: {shortDate(mo.moveDate)}{Number(mo.onHold) === 1 ? " · on hold" : Number(mo.issues) === 1 ? " · flagged" : ""} →
                </Link>
              ))}
            </Box>
          ) : (
            <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 12.5, color: "#c62828" }}>No move-out filed for this tenant.</Typography>
              <Button size="small" variant="outlined" color="warning" onClick={onCreateMoveOut} disabled={creatingMoveOut} sx={{ textTransform: "none", py: 0 }}>
                {creatingMoveOut ? "Creating…" : "Create move-out"}
              </Button>
            </Box>
          )
        ) : null}
      </Box>

      <HandoffBlock kind={kind} handoff={handoff} />

      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
        {unitMoves.length ? "Other moves on this unit" : "No other moves on this unit in the last 90 days or next 60"}
      </Typography>
      {unitMoves.map((m) => <MoveLink key={`${m.kind}-${m.uuid}`} m={m} today={today} />)}

      {unitLastBill ? (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Last bill on this unit</Typography>
          <Typography sx={{ fontSize: 13 }}>
            {money(unitLastBill.total)} · {longDate(unitLastBill.billDate)} · {unitLastBill.name || `Acct #${unitLastBill.accountNum}`}
            {unitLastBill.type ? ` · ${BILL_TYPE_LABELS[unitLastBill.type] || unitLastBill.type}` : ""}
          </Typography>
        </>
      ) : null}
    </Panel>
  );
}

export function NotesPanel({ notes, loading, onAdd, formatDateTime }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? notes : notes.slice(0, 4);
  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    const ok = await onAdd(text.trim());
    setSaving(false);
    if (ok) setText("");
  };
  return (
    <Panel title={`Notes${notes.length ? ` (${notes.length})` : ""}`}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={6}
          placeholder="Add a note"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) add(); }}
        />
        <Button variant="outlined" size="small" onClick={add} disabled={saving || !text.trim()} sx={{ textTransform: "none", mt: 0.25 }}>Add</Button>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} /></Box>
      ) : notes.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>No notes yet.</Typography>
      ) : (
        <>
          {shown.map((note, i) => (
            <Box key={note.id || i} sx={{ py: 0.75, borderTop: i ? 1 : 0, borderColor: "divider" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{note.createdByName || note.creator || "Unknown"}</Typography>
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{formatDateTime(note.created)}</Typography>
              </Box>
              <Typography sx={{ fontSize: 13, whiteSpace: "pre-line" }}>{note.note}</Typography>
            </Box>
          ))}
          {notes.length > shown.length ? (
            <Button size="small" onClick={() => setShowAll(true)} sx={{ textTransform: "none", mt: 0.5 }}>Show all {notes.length}</Button>
          ) : null}
        </>
      )}
    </Panel>
  );
}
